import { CardNumbering } from "../types/transactions_query";

export interface ClientBaseUser {
  uid: string;
  isGlobalAdmin: boolean;
  requiresReinforcedLogin?: boolean;
  permissions: ClientPermission[];
}

export interface ClientUserList {
  users: ClientUser[];
}

export interface ClientUser extends ClientBaseUser {
  displayName: string;
  email: string;
  emailVerified: boolean;
  disabled: boolean;
  creationTimeUTC?: string;
  lastSignInTimeUTC?: string;
}

export interface UserCustomClaims {
  requiresReinforcedLogin: boolean;
  reinforcedLogin: boolean;
  isToken: boolean;
}

export interface AuditUserSignInQuery {
  limit: number;
  uid: string;
  from: string;
  to: string;
}

export interface UserAuditRecord {
  uid: string;
  email: string;
  signInTimeUTC: string;
  ips: string;
  userAgent?: string;
  claims: Record<string, unknown>;
  allIPs: Record<string, string>;
  eventName?: string;
  otherDetails?: string;
}

export const PERMISSION_ATTRS: (keyof BasePermission)[] = [
  "allowTxnStatistics",
  "allowConnectionGraph",
  "paymentLink",
  "clientAPI",
  "txnModification",
  "txnView",
  "vendingAdmin",
  "topayAdmin",
  "euroshellCertificate"
];

// Permissions that can only be granted explicitly (never inherited from "admin") and
// can only be edited by a token/support user or a global admin.
export const SPECIAL_PERMISSION_ATTRS: (keyof BasePermission)[] = [
  "paymentLink",
  "clientAPI",
  "txnModification",
  "vendingAdmin",
  "topayAdmin"
];

export interface BasePermission {
  admin: boolean;
  allowTxnStatistics: boolean;
  allowConnectionGraph: boolean;
  paymentLink: boolean;
  clientAPI: boolean;
  txnModification: boolean;
  txnView: boolean;
  vendingAdmin: boolean;
  topayAdmin: boolean;
  euroshellCertificate: boolean;
}

export interface ClientPermission extends BasePermission {
  companyID?: number;
  branchIDs: number[];
  isToken: boolean;
}

export interface BaseCompany {
  id: number;
  name: string;
  city?: string;
  province?: string;
  postCode?: string;
}

export interface BranchSummary {
  id: number;
  name: string;
  customerID?: string;
}

export interface BaseBranch extends BranchSummary {
  postCode?: string;
  city?: string;
  province?: string;
  paymentLink: boolean;
  isVending: boolean;
}

export interface CompanyList {
  companies: ResultCompanySmall[];
}

export interface ResultCompanySmall extends BaseCompany {
  addressLine1?: string;
  addressLine2?: string;
  branches?: BranchSummary[];
}

export interface ResultCompanyFull extends BaseCompany {
  addressLine1?: string;
  addressLine2?: string;
  branches?: ResultBranch[];
}

export interface ResultBranch extends BaseBranch {
  addressLine1: string;
  addressLine2: string;
  terminals: ResultTerminal[];
  devices?: Device[];
  manualClose: boolean;
  commerceCodes?: CommerceCodes[];
}

export interface Device {
  connectedTimestamp?: JDateTime;
  hardware: string; // "Spire SPp30 USB"
  deviceModel: string; // "30"
  status: string; // "connected"
  manufacturerCode: string; // "92"
  serialNumber?: number; // 74211777
  softwareVersion: string; // "52 - PUP: 15"
  statusText: string; // "Conectado "
  connectedToTCOD?: string;
}

export interface JDate {
  day: number;
  month: number;
  year: number;
}

export function jDateToString(ts?: JDate): string | undefined {
  if (!ts) {
    return undefined;
  }
  return ts.year + "-" + padTen(ts.month) + "-" + padTen(ts.day);
}

export function jDateTimeToString(ts?: JDateTime): string | undefined {
  if (!ts) {
    return undefined;
  }
  return (
    jDateToString(ts) + " " + padTen(ts.hour) + ":" + padTen(ts.minute) + ":" + padTen(ts.second) + "." + ts.millisecond
  );
}

function padTen(n: number): string {
  return n <= 9 ? "0" + n : n.toString();
}

export interface JDateTime extends JDate {
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

export interface ResultTerminal {
  code: string;
  name: string;
  tcod: string;
  activationTimestamp: string;
  lastConnection: string;
  releaseTimestamp: string;
  connectedPinPads: string;
  delayMinutes: string;
  machineAndLocalIP: string | null;
  lastIP: string;
  unlockCode: string;
}

export interface CompanyExtraDetailsQuery {
  companyID: number;
}

export interface CompanyExtraDetails {
  companyID: number;
  branding?: CompanyBranding;
}

export interface CompanyBranding {
  mobileVendorLogo: string | null;
  mobileReceiptLogo: string | null;
}

export interface TokenValidation {
  authToken: string;
  companyID: number;
  companyExists: boolean;
}

export interface ActivationQRRequest {
  branchID: number;
  terminalID: number;
  tcod: string;
  language: string;
  generateQR?: boolean;
}

export interface ActivationQRData {
  branchID: number;
  terminalID: number;
  tcod: string;
  networkConfiguration?: string | null;
  language: string;
  qrData?: string;
}

export interface ActivationQRPacked {
  b: number; // branchID,
  t: number; // terminalID,
  c: string; // tcod
  n?: string; //networkConfiguration
}

export interface ManagementResponse {
  globalUnlockCode: string;
}

export interface DebugTerminal {
  id: string;
  name: string;
  app: string;
}

export interface ParamsDebugTerminal extends DebugTerminal {
  action: "save" | "delete";
}

/** Terminal Data - Begin */
export interface TerminalDataRequest {
  companyID: number;
  branchID: number;
  terminalID: number;
  tcod: string;
  date: string;
  language: string;
}
export interface TerminalDataResponse {
  query: {
    company: number;
    branch: number;
    terminal: number;
    tcod: string;
    date: string;
  };
  terminalStatuses: TerminalStatus[];
  file: ApiFileResult;
}
export interface TerminalStatus {
  time: string;
  latency: number;
  pinpads: number;
}
export interface ApiFileResult {
  mimeType: string;
  base64Data: string;
}
/** Terminal Data - End */

/** Terminal Rename - Begin */
export interface TerminalRenameRequest {
  companyID: number;
  branchID: number;
  tcod: string;
  newName: string;
}
export interface TerminalRenameResponse {
  query: {
    company: number;
    branch: number;
    tcod: string;
    newName: string;
  };
  result: { success: boolean };
}
/** Terminal Rename - End */

/** Register User types - Begin */
export interface RegisterUser {
  newUser: boolean;
  email: string;
  password?: string;
  displayName?: string;
  companyID?: number;
  paytefUser?: string;
  paytefPassword?: string;
}
export interface RegisterUserResult {
  uid: string;
  company?: ResultCompanySmall;
}
/** Register User types - End */

/** User Set types - Begin */
export interface EditUser {
  edit: boolean;
  uid?: string;
  accountChanges?: AccountChanges;
  permissions?: EditPermission[];
  // True when this save comes from the bulk Excel import — special permissions
  // (SPECIAL_PERMISSION_ATTRS) are always ignored on import, regardless of caller.
  isImport?: boolean;
}
export interface AccountChanges {
  // New user
  email?: string;
  // Edit user
  disabled?: boolean;
  // Both
  displayName?: string;
  password?: string;
  emailVerified?: boolean;
}
export interface EditPermission extends Partial<BasePermission> {
  companyID: number;
  remove: boolean;
  branchIDs?: number[];
}
export interface EditUserResult {
  success: boolean;
  isNewAccount: boolean;
  user: ClientUser;
}
export interface UserConfig {
  publicApiEnabled: boolean;
  publicApiKey: string;
}
export interface UserConfigChange {
  action?: "newkey";
  publicApiEnabled: boolean;
}
/** User Set types - End */

/** User Query types - Begin */
export interface UserQueryFilter {
  queryAll: boolean;
  companyID?: number;
  userUID?: string;
}
/** User Query types - End */

export interface ExcelUser {
  displayName: string;
  email: string;
  permissionScore: string;
  branchIDs: string;
  lastSignInTimeUTC: string;
}

/** Transaction services - Begin */
export interface TransactionExecute {
  companyID: number;
  branchID: number;
  tcod: string;
  previousTransactionID: null | number;
  type: TransactionType;
  amountCents: number;
  reference: string;
  comment: string;
  language: string;
  expiryDate?: string;
  cardNumber?: string;
  commerceCodeID?: number;
}

export interface CommerceCodesRequest {
  companyID: number;
  branchID: number;
  terminalID: number;
  tcod: string;
  language: string;
}

export interface CommerceCodes {
  code: string;
  active: boolean;
  id: number | null;
  csb: string;
  terminal?: number;
  name: string;
}

export interface TransactionExecuteResult {
  query: TransactionExecute;
  transaction: Transaction;
}

export interface Refund {
  date: string;
  amount: string;
  requestId: string;
}

export interface BizumRefundResult {
  status: "ok" | "ko";
  message: string;
  refunds: Refund[];
}

export interface TransactionQuery {
  companyID: number;
  branchID?: number;
  tcod?: string;
  from: string;
  to: string;
  reference: string;
  transactionID?: number;
  bin?: string;
  cardType?: string;
  authCode?: string;
  pageNum?: number;
  amountFrom?: number;
  amountTo?: number;
  language: string;
  useLocalTime?: boolean;
}
export interface TransactionQueryResult {
  query: {
    company: number;
    branch?: number;
    tcod?: string;
    from: string;
    to: string;
    reference?: string;
    transactionID?: number;
    pageNum?: number;
    amountFrom?: number;
    amountTo?: number;
  };
  morePages?: boolean;
  transactions: Transaction[];
  transactionChanges: TransactionChange[];
}
export interface TransactionChange {
  originalTxnID: number | null;
  newTxnID: number;
  executeData: TransactionExecute;
  timestamp: string;
  userUID: string;
  userName: string;
}
export interface TransactionInfoQuery {
  companyID: number;
  branchID?: number;
  transactionID: string;
}
export interface TransactionInfoResult {
  query: {
    company: number;
    branch?: number;
    transactionID: string;
  };
  transaction?: Transaction;
}

export interface ManualCloseRequest {
  companyID: number;
  branchID: number;
  tcod: string;
  terminalID: number;
  action: "closeBranch" | "closeTerminal";
}

export interface ManualCloseResponse {
  result: string;
  transactions: string[];
  branchData: {
    customerID: string;
    city: string;
    id: number;
    manualClose: true;
    postCode: string;
    name: string;
    addressLine2: string;
    addressLine1: string;
  };
}
export type TXFileType = "resume" | "statistics" | "listing";
export interface TransactionQueryFile extends TransactionQuery {
  fileType: TXFileType;
}

export type TransactionType =
  | "sale"
  | "saleAC"
  | "refund"
  | "refundAC"
  | "auth"
  | "authClose"
  | "authCancel"
  | "test"
  | "cancel"
  | "authTopup"
  | "token";

export type CardTypeCode = "" | "AX" | "DC" | "DV" | "ES" | "GA" | "JC" | "MA" | "MC" | "TL" | "UP" | "VI" | "SR";

export interface Transaction {
  id?: string;
  requestedAmount?: string;
  timestamp?: string;
  amountWithSign?: string;
  resultText?: string;
  authorisationCode?: string;
  type?: TransactionType;
  operationTypeName?: string;
  transactionReference?: string;
  commerceTCOD?: string;
  approved?: string;
  failed?: string;
  resultCode?: string;
  commerceCodeNumber?: string;
  cardTypeCode?: CardTypeCode;
  binNumber?: string;
  veryHiddenCardNumber?: string;
  cardNumbering?: CardNumbering;
  sessionDate?: string;
  pinPad?: string;
  dcc?: string;
  country?: string;
  branchID?: string;
  branchName?: string;
  dataEntryCode?: string;
  hcpName?: string;
  t1?: string;
  t2?: string;
  cardHolder?: string;
  creditOrDebit?: string;
  processingServer?: string;
  processingStatus?: string;
  bankName?: string;
  totalCost?: string;
  issuerNameAndCountry?: string;
  dataEntryText?: string;
  isCorporate?: string;
  receiptText?: string;
  signatureText?: string;
  signatureImage?: string;
  company?: string;
  companyId?: number;
  previousTransactionID?: number;
  referencedTransactions?: {
    previousTransaction?: number;
    closedByTransaction?: number;
  };
}

export const CARD_TYPES: { [key in CardTypeCode]: string } = {
  VI: "Visa",
  MC: "MasterCard",
  MA: "Maestro",
  JC: "JCB",
  UP: "Union Pay",
  AX: "American Express",
  DC: "Diners Club",
  GA: "Gasoleo Bonificado",
  ES: "EuroShell",
  TL: "Recarga Movil",
  DV: "Discover",
  SR: "StarRessa",
  "": "None"
};

export function getCardTypeName(cardTypeCode: CardTypeCode): string {
  if (CARD_TYPES[cardTypeCode]) {
    return CARD_TYPES[cardTypeCode];
  }
  return cardTypeCode;
}
/** Transaction services - End */

export interface Certificate {
  date: string;
  cardNumber: string;
  from: string;
  to: string;
  page: number;
  saved: boolean;
  created?: number;
}
